import { Language, User, useUserQuery } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@spotted-zebra-uk/ui-components';
import EditUserInfo from './EditUserInfo/EditUserInfo';
import UserMainReset from './UserMainResets/UserMainReset';
import { UserProjectRoles } from './UserProjectRoles/UserProjectRoles';
import UserRoles from './UserRoles/UserRoles';

export interface IUserPath {
  userId: string;
}
export interface IRole {
  companyId?: number;
  externalId?: string;
  id: number;
  role: {
    id: number;
    name: string;
  };
}
export interface IUserInfo {
  firstName: string;
  lastName: string;
  email: string;
  subId?: string;
  id: number;
  roles: IRole[];
  language: Language;
  twoFactorEnabled: boolean;
}

enum UserDetailsTab {
  ROLES = 'roles',
  PROJECT_ROLES = 'project_roles',
  ASSESSMENTS = 'assessments',
}

const getIndexFromTab = (tab: UserDetailsTab): number => {
  const tabIndexMap = {
    [UserDetailsTab.ROLES]: 0,
    [UserDetailsTab.PROJECT_ROLES]: 1,
    [UserDetailsTab.ASSESSMENTS]: 2,
  };
  return tabIndexMap[tab];
};

const getTabFromIndex = (index: number): UserDetailsTab => {
  const indexTabMap = {
    0: UserDetailsTab.ROLES,
    1: UserDetailsTab.PROJECT_ROLES,
    2: UserDetailsTab.ASSESSMENTS,
  };
  return indexTabMap[index as keyof typeof indexTabMap] || UserDetailsTab.ROLES;
};

const UserDetails = () => {
  const { userId } = useParams() as unknown as IUserPath;
  const [selectedTab, setSelectedTab] = useState<UserDetailsTab>(
    UserDetailsTab.ROLES,
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize search from URL params on component mount
  useEffect(() => {
    const tabFromParams = searchParams.get('tab');
    if (tabFromParams) {
      setSelectedTab(tabFromParams as UserDetailsTab);
    } else {
      setSelectedTab(UserDetailsTab.ROLES);
    }
  }, [searchParams]);

  const { data } = useUserQuery({
    variables: { id: Number(userId) },
  });

  if (data?.User) {
    const userInfo = data.User as User;
    return (
      <div>
        <EditUserInfo userInfo={userInfo} />
        <Tabs
          onSelectTab={(index) => {
            setSelectedTab(getTabFromIndex(index));
            setSearchParams({ tab: getTabFromIndex(index) });
          }}
          selectedTabIndex={getIndexFromTab(selectedTab)}
        >
          <TabList>
            <Tab>Roles</Tab>
            <Tab>Hiring Roles</Tab>
            <Tab>Assessments</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {selectedTab === UserDetailsTab.ROLES && (
                <UserRoles userInfo={userInfo} />
              )}
            </TabPanel>
            <TabPanel>
              {selectedTab === UserDetailsTab.PROJECT_ROLES && (
                <UserProjectRoles userId={Number(userId)} />
              )}
            </TabPanel>
            <TabPanel>
              {selectedTab === UserDetailsTab.ASSESSMENTS && (
                <UserMainReset userInfo={userInfo} />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    );
  }
  return null;
};

export default UserDetails;
